import { BaseComponent } from "utils/base-component"
import "./index.scss"

type Snapshot = {
  issued?: string
  image: string
  thumbnail: string
}

type Webcam = {
  snapshots?: Snapshot[]
  video?: string
}

type WebcamArchiveConfig = {
  imageAlt?: string
  issuedLabel?: string
  scrollIntoViewIfNeeded?: () => void
  title?: string
}

type WebcamArchiveLocale = {
  imageAlt?: string
  issuedLabel?: string
  title?: string
}

export class WebcamArchive extends BaseComponent<WebcamArchiveConfig> {
  static cssClass = "webcam-archive"

  private archivePlayer?: HTMLVideoElement
  private image!: HTMLImageElement
  private issuedLabelNode?: Text
  private locale: WebcamArchiveLocale = {}
  private mediaContainer!: HTMLElement
  private photoIndex = 0
  private playButton?: HTMLElement
  private titleEl?: HTMLElement

  init() {
    this.ensureMarkup()
    this.prepareMedia()
    this.reset()
  }

  render(webcam: Webcam, locale: WebcamArchiveLocale = {}) {
    this.setLocale(locale)
    this.root.classList.remove(this.modifierClassName("hidden"))

    if (webcam.video) {
      this.renderArchivePlayer(webcam.video)
    } else if (this.archivePlayer) {
      this.archivePlayer.removeAttribute("src")
      this.archivePlayer.load()
    }

    this.renderArchiveThumbs(webcam)
    this.selectArchivePhoto(webcam, 0)
  }

  reset() {
    this.hideAndResetArchivePlayer()
    this.root.classList.add(this.modifierClassName("hidden"))
    this.image.classList.add(this.elementModifierClassName("webcam", "hidden"))
    this.image.alt = ""
    this.mediaContainer.classList.remove(this.elementModifierClassName("media", "loading"))
    this.mediaContainer.classList.remove(this.elementModifierClassName("media", "error"))

    const issued = this.root.querySelector(this.elementClassSelector("issued"))
    issued?.classList.add(this.elementModifierClassName("issued", "hidden"))

    if (this.archivePlayer) {
      this.archivePlayer.classList.add(this.elementModifierClassName("archive-player", "hidden"))
    }
  }

  hideAndResetArchivePlayer() {
    if (!this.archivePlayer) return

    this.archivePlayer.classList.add(this.elementModifierClassName("archive-player", "hidden"))
    this.archivePlayer.pause()
    this.archivePlayer.currentTime = 0
  }

  private prepareMedia() {
    this.image = this.root.querySelector(this.elementClassSelector("webcam")) as HTMLImageElement
    this.mediaContainer = this.root.querySelector(this.elementClassSelector("media")) as HTMLElement

    this.image.addEventListener("load", () => {
      this.mediaContainer.classList.remove(this.elementModifierClassName("media", "loading"))
      this.mediaContainer.classList.remove(this.elementModifierClassName("media", "error"))
    })

    this.image.addEventListener("error", () => {
      this.mediaContainer.classList.remove(this.elementModifierClassName("media", "loading"))
      this.mediaContainer.classList.add(this.elementModifierClassName("media", "error"))
    })
  }

  private renderArchivePlayer(videoUrl: string) {
    this.hideAndResetArchivePlayer()

    if (!this.archivePlayer) {
      const player = document.createElement("video")
      player.classList.add(this.elementClassName("archive-player"))
      player.src = videoUrl
      player.controls = true
      player.addEventListener("play", () => this.updatePlayButton(true))
      player.addEventListener("pause", () => this.updatePlayButton(false))
      this.mediaContainer.appendChild(player)
      this.archivePlayer = player
    } else {
      this.archivePlayer.src = videoUrl
      this.archivePlayer.classList.remove(this.elementModifierClassName("archive-player", "hidden"))
    }
  }

  private renderArchiveThumbs(webcam: Webcam) {
    const { snapshots, video } = webcam
    const { archiveThumbs } = this
    archiveThumbs.innerHTML = ""
    this.playButton = undefined

    if (!snapshots || snapshots.length === 0) {
      this.root.classList.add(this.modifierClassName("hidden"))
      return
    }

    if (video) {
      const thumb = this.renderArchiveTimelapseThumb()
      archiveThumbs.appendChild(thumb)
    }

    snapshots.forEach((snapshot, index) => {
      const thumb = this.renderArchiveThumb(webcam, snapshot, index)
      archiveThumbs.appendChild(thumb)
    })
  }

  private renderArchiveThumb(webcam: Webcam, snapshot: Snapshot, index: number) {
    const item = document.createElement("li")
    item.classList.add(this.elementClassName("webcam-list-item"))
    item.classList.add(this.elementModifierClassName("webcam-list-item", "loading"))
    item.dataset.index = String(index)

    const archived = document.createElement("div")
    archived.classList.add(this.elementClassName("archived-time"))
    archived.innerText = snapshot.issued || ""

    item.appendChild(archived)

    const img = new Image()
    img.src = snapshot.thumbnail
    img.classList.add(this.elementClassName("webcam-thumbnail"))

    img.addEventListener("load", () => {
      item.classList.remove(this.elementModifierClassName("webcam-list-item", "loading"))
    })

    img.addEventListener("error", () => {
      item.classList.remove(this.elementModifierClassName("webcam-list-item", "loading"))
      item.classList.add(this.elementModifierClassName("webcam-list-item", "error"))
    })

    item.appendChild(img)

    item.addEventListener("click", (e) => {
      e.preventDefault()

      this.selectArchivePhoto(webcam, index)
      this.config.scrollIntoViewIfNeeded?.()
    })

    return item
  }

  private showArchivePlayer() {
    const active = this.archiveThumbs.querySelector(
      this.elementClassSelector("webcam-list-item", "active"),
    )
    if (active) {
      active.classList.remove(this.elementModifierClassName("webcam-list-item", "active"))
    }

    this.image.classList.add(this.elementModifierClassName("webcam", "hidden"))
    this.mediaContainer.classList.remove(this.elementModifierClassName("media", "loading"))
    this.archivePlayer?.classList.remove(this.elementModifierClassName("archive-player", "hidden"))

    const issued = this.root.querySelector(this.elementClassSelector("issued"))
    issued?.classList.add(this.elementModifierClassName("issued", "hidden"))
  }

  private renderArchiveTimelapseThumb() {
    const thumb = document.createElement("li")
    thumb.classList.add(this.elementClassName("webcam-list-item"))

    const playButton = document.createElement("div")
    playButton.classList.add(this.elementClassName("play-button"))
    playButton.innerText = "Play"
    this.playButton = playButton

    thumb.appendChild(playButton)

    playButton.addEventListener("click", (e) => {
      e.preventDefault()

      if (playButton.classList.contains(this.elementModifierClassName("play-button", "playing"))) {
        this.archivePlayer?.pause()
      } else {
        this.showArchivePlayer()
        this.config.scrollIntoViewIfNeeded?.()
        this.archivePlayer?.play()
      }
    })

    return thumb
  }

  private selectArchivePhoto(webcam: Webcam, index: number) {
    this.hideAndResetArchivePlayer()

    this.photoIndex = index

    this.image.classList.remove(this.elementModifierClassName("webcam", "hidden"))
    this.mediaContainer.classList.add(this.elementModifierClassName("media", "loading"))
    this.mediaContainer.classList.remove(this.elementModifierClassName("media", "error"))
    const snapshot = webcam.snapshots?.[this.photoIndex]
    if (!snapshot) return

    this.image.src = snapshot.image
    this.image.alt = index === 0 ? this.imageAlt || this.image.dataset.alt || "" : ""

    const issued = this.root.querySelector(this.elementClassSelector("issued"))
    if (snapshot.issued) {
      issued?.classList.remove(this.elementModifierClassName("issued", "hidden"))
      const value = issued?.querySelector(this.elementClassSelector("issued-value"))
      if (value) value.textContent = snapshot.issued
    } else {
      issued?.classList.add(this.elementModifierClassName("issued", "hidden"))
    }

    const active = this.archiveThumbs.querySelector(
      this.elementClassSelector("webcam-list-item", "active"),
    )
    if (active) {
      active.classList.remove(this.elementModifierClassName("webcam-list-item", "active"))
    }

    const selector = `${this.elementClassSelector("webcam-list-item")}[data-index='${
      this.photoIndex
    }']`
    const selectedThumbnail = this.archiveThumbs.querySelector(selector)
    selectedThumbnail?.classList.add(this.elementModifierClassName("webcam-list-item", "active"))
  }

  private updatePlayButton(playing: boolean) {
    if (!this.playButton) return

    this.playButton.classList.toggle(
      this.elementModifierClassName("play-button", "playing"),
      playing,
    )
    this.playButton.innerText = playing ? "Pause" : "Play"
  }

  private get archiveThumbs() {
    return this.root.querySelector(this.elementClassSelector("webcam-list")) as HTMLElement
  }

  private ensureMarkup() {
    this.ensureMediaMarkup()
    this.ensureTitleMarkup()
    this.ensureTimelineMarkup()
  }

  private ensureTitleMarkup() {
    if (this.root.querySelector(this.elementClassSelector("title"))) return

    const { title } = this
    if (!title) return

    const titleEl = document.createElement("h3")
    titleEl.classList.add(this.elementClassName("title"))
    titleEl.textContent = title
    this.titleEl = titleEl

    const timeline = this.root.querySelector(this.elementClassSelector("timeline"))
    this.root.insertBefore(titleEl, timeline)
  }

  private ensureMediaMarkup() {
    if (this.root.querySelector(this.elementClassSelector("media"))) return

    const media = document.createElement("div")
    media.classList.add(this.elementClassName("media"))

    const image = document.createElement("img")
    image.classList.add(this.elementClassName("webcam"))
    image.classList.add(this.elementModifierClassName("webcam", "hidden"))
    image.dataset.alt = this.imageAlt
    image.alt = this.imageAlt
    media.appendChild(image)

    const issued = document.createElement("span")
    issued.classList.add(this.elementClassName("issued"))
    issued.classList.add(this.elementModifierClassName("issued", "hidden"))
    this.issuedLabelNode = document.createTextNode(`${this.issuedLabel} `)
    issued.appendChild(this.issuedLabelNode)

    const issuedValue = document.createElement("span")
    issuedValue.classList.add(this.elementClassName("issued-value"))
    issued.appendChild(issuedValue)
    media.appendChild(issued)

    const timeline = this.root.querySelector(this.elementClassSelector("timeline"))
    this.root.insertBefore(media, timeline)
  }

  private get imageAlt() {
    return this.locale?.imageAlt || this.config.imageAlt || this.root.dataset.imageAlt || ""
  }

  private get issuedLabel() {
    return (
      this.locale?.issuedLabel ||
      this.config.issuedLabel ||
      this.root.dataset.issuedLabel ||
      "Issued"
    )
  }

  private get title() {
    return this.locale?.title || this.config.title || this.root.dataset.title || ""
  }

  private setLocale(locale: WebcamArchiveLocale) {
    this.locale = locale

    if (!this.titleEl) {
      this.ensureTitleMarkup()
    }

    if (this.titleEl) {
      this.titleEl.textContent = this.title
    }

    if (this.issuedLabelNode) {
      this.issuedLabelNode.textContent = `${this.issuedLabel} `
    }
  }

  private ensureTimelineMarkup() {
    if (this.root.querySelector(this.elementClassSelector("webcam-list"))) return

    const timeline = document.createElement("div")
    timeline.classList.add(this.elementClassName("timeline"))

    const list = document.createElement("ul")
    list.classList.add(this.elementClassName("webcam-list"))
    timeline.appendChild(list)

    const timelineBar = document.createElement("div")
    timelineBar.classList.add(this.elementClassName("timeline-bar"))
    timeline.appendChild(timelineBar)

    this.root.appendChild(timeline)
  }

  private elementClassSelector(name: string, mod = "") {
    return `.${this.elementModifierClassName(name, mod)}`
  }

  private elementModifierClassName(name: string, mod = "") {
    return `${this.elementClassName(name)}${mod ? `--${mod}` : ""}`
  }
}
